import { ItemType } from "../../../clients/graphqlTypes.js";
import { getDefaultCreatableSubItemTypes } from "./constraints.js";

// ---------------------------------------------------------------------------
// Generic tree item interfaces
// ---------------------------------------------------------------------------

export interface TreeItemBase {
  id: string;
  parentId?: string | null;
}

export interface MoveTargetItem extends TreeItemBase {
  type: ItemType;
  linkedQuoteTemplate?: { id: string } | null;
}

// ---------------------------------------------------------------------------
// canHaveCreateInput
// ---------------------------------------------------------------------------

export const canHaveCreateInput = (
  itemType: ItemType,
  documentItemTypes: ItemType[],
  itemHasLinkedTemplate: boolean
): boolean => {
  if (itemHasLinkedTemplate) return false;
  return (
    getDefaultCreatableSubItemTypes(itemType, documentItemTypes).length > 0
  );
};

// ---------------------------------------------------------------------------
// isDescendantOf — checks if candidateId is a descendant of ancestorId
// ---------------------------------------------------------------------------

export function isDescendantOf<T extends TreeItemBase>(
  candidateId: string,
  ancestorId: string,
  items: T[]
): boolean {
  const itemsById = new Map(items.map(i => [i.id, i]));
  let current = itemsById.get(candidateId);
  while (current?.parentId) {
    if (current.parentId === ancestorId) return true;
    current = itemsById.get(current.parentId);
  }
  return false;
}

// ---------------------------------------------------------------------------
// getValidMoveTargets — filters items to valid parent candidates for a set of items
// ---------------------------------------------------------------------------

export function getValidMoveTargets<T extends MoveTargetItem>(
  items: T[],
  itemIds: string[],
  documentItemTypes: ItemType[]
): T[] {
  const itemSet = new Set(itemIds);
  const selectedItems = items.filter(i => itemSet.has(i.id));
  const selectedTypes = selectedItems
    .map(i => i.type)
    .filter(Boolean) as ItemType[];

  // If all items share the same parent, exclude that parent
  const parentIds = new Set(selectedItems.map(i => i.parentId).filter(Boolean));
  const commonParentId = parentIds.size === 1 ? [...parentIds][0] : null;

  return items.filter(candidate => {
    if (itemSet.has(candidate.id)) return false;
    if (candidate.linkedQuoteTemplate) return false;
    if (commonParentId && candidate.id === commonParentId) return false;

    const creatableTypes = getDefaultCreatableSubItemTypes(
      candidate.type,
      documentItemTypes
    );
    if (!selectedTypes.every(ct => creatableTypes.includes(ct))) return false;

    for (const id of itemIds) {
      if (isDescendantOf(candidate.id, id, items)) return false;
    }

    return true;
  });
}

// ---------------------------------------------------------------------------
// filterRedundantChildren — removes children whose parent already covers them
// ---------------------------------------------------------------------------

/**
 * When a selected item has ALL its direct children also selected, those
 * children are redundant — moving the parent already moves the entire subtree.
 * This applies recursively: if a grandparent subsumes a parent, and the parent
 * subsumes its children, all descendants are removed.
 *
 * The check is performed in one pass (collect all items to remove), then
 * filtered at the end so that multi-level removals are consistent.
 */
export function filterRedundantChildren<T extends TreeItemBase>(
  selectedIds: string[],
  items: T[]
): string[] {
  const selectedSet = new Set(selectedIds);

  // Build children lookup: parentId → child items
  const childrenByParentId = new Map<string, T[]>();
  for (const item of items) {
    if (item.parentId) {
      let children = childrenByParentId.get(item.parentId);
      if (!children) {
        children = [];
        childrenByParentId.set(item.parentId, children);
      }
      children.push(item);
    }
  }

  // Collect all children to remove in one pass
  const toRemove = new Set<string>();
  for (const id of selectedIds) {
    const children = childrenByParentId.get(id);
    if (!children || children.length === 0) continue;
    if (children.every(child => selectedSet.has(child.id))) {
      for (const child of children) {
        toRemove.add(child.id);
      }
    }
  }

  return selectedIds.filter(id => !toRemove.has(id));
}

// ---------------------------------------------------------------------------
// sortItemIdsByTreeOrder — sorts item IDs by their path in the tree
// ---------------------------------------------------------------------------

export function sortItemIdsByTreeOrder(
  itemIds: string[],
  items: { id: string; pathSortable: string }[]
): string[] {
  const itemsById = new Map(items.map(i => [i.id, i]));
  return [...itemIds].sort((a, b) => {
    const itemA = itemsById.get(a);
    const itemB = itemsById.get(b);
    if (!itemA || !itemB) return 0;
    return itemA.pathSortable.localeCompare(itemB.pathSortable);
  });
}
